import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Package, Wallet, Percent, Clock3 } from 'lucide-react';
import { fetchPackageDetails, getCurrentUserInPackage } from '@/api/Admin/userPackage';
import { fetchAllUser } from '@/api/Admin/User';
import { addUserToPackage } from '@/api/Admin/adminPackage';
import { ListUserAPI, UserInPackageAPI } from '@/api/Admin/type';

interface PackageItem {
	id: number;
	name: string;
	description: string | null;
	price: string;
	total_slots: number;
	remaining_slots: number;
	sold_slots?: number;
	monthly_commission_rate: string;
	min_profit_rate: string;
	duration_months: number;
	is_active: boolean;
	type: string;
	created_at: string;
	updated_at: string;
	parent?: {
		id: number;
		name: string;
		remaining_slots: number;
	};
}

interface UserRow {
	id: number;
	fullname?: string;
	full_name_display?: string;
	email: string;
	avatar?: string | null;
	cccd?: string | null;
	cccd_number?: string | null;
}

type CurrentPackageUser = UserInPackageAPI['data'][number];

const fmtVND = (v: number) => new Intl.NumberFormat('vi-VN').format(Math.round(v)) + ' đ';

export default function PlanDetailPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { Id } = useParams<{ Id: string }>();

	const [pkg, setPkg] = useState<PackageItem | null>(null);
	const [loading, setLoading] = useState(false);
	const [users, setUsers] = useState<UserRow[]>([]);
	const [usersLoading, setUsersLoading] = useState(false);
	const [currentUsers, setCurrentUsers] = useState<CurrentPackageUser[]>([]);
	const [currentUsersLoading, setCurrentUsersLoading] = useState(false);
	const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
	const [assigning, setAssigning] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	const loadPackage = async (id: number) => {
		setLoading(true);
		try {
			const data = await fetchPackageDetails(id);
			setPkg(data as unknown as PackageItem);
		} catch {
			message.error(t('plan.pageDetail.messages.loadPackageError'));
			navigate('/admin/plans');
		} finally {
			setLoading(false);
		}
	};

	const loadUsers = async () => {
		setUsersLoading(true);
		try {
			const response: ListUserAPI = await fetchAllUser(1, 10000);
			if (response.status) {
				setUsers((response.data.items || []) as UserRow[]);
			}
		} catch {
			message.error(t('plan.pageDetail.messages.loadUsersError'));
		} finally {
			setUsersLoading(false);
		}
	};

	const loadCurrentUsers = async (packageId: number) => {
		setCurrentUsersLoading(true);
		try {
			const response = await getCurrentUserInPackage(packageId);
			if (response.status) {
				setCurrentUsers(response.data || []);
				return;
			}
			setCurrentUsers([]);
		} catch {
			message.error(t('plan.currentUsers.loadError'));
		} finally {
			setCurrentUsersLoading(false);
		}
	};

	const pkgId = useMemo(() => {
		const parsed = Number(Id);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	}, [Id]);

	useEffect(() => {
		if (!pkgId) {
			message.warning(t('plan.pageDetail.messages.invalidPackageId'));
			navigate('/admin/plans');
			return;
		}

		loadPackage(pkgId);
		loadUsers();
		loadCurrentUsers(pkgId);
	}, [pkgId, navigate]);

	const toggleUser = (userId: number) => {
		setSelectedUserIds(prev =>
			prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
		);
	};

	const handleAssignUsers = async () => {
		if (!pkg) {
			message.warning(t('plan.pageDetail.messages.packageNotFound'));
			return;
		}
		if (selectedUserIds.length === 0) {
			message.warning(t('plan.pageDetail.messages.selectAtLeastOneUser'));
			return;
		}
		if (selectedUserIds.length > pkg.remaining_slots) {
			message.warning(t('plan.pageDetail.messages.exceedRemainingSlots', { count: pkg.remaining_slots }));
			return;
		}

		setAssigning(true);
		try {
			await addUserToPackage(selectedUserIds, String(pkg.id));
			message.success(t('plan.pageDetail.messages.assignSuccess'));
			setSelectedUserIds([]);
			await Promise.all([loadPackage(pkg.id), loadCurrentUsers(pkg.id)]);
		} catch {
			message.error(t('plan.pageDetail.messages.assignError'));
		} finally {
			setAssigning(false);
		}
	};

	const parentPrice = pkg ? parseFloat(pkg.price) : 0;
	const soldSlots = pkg ? (pkg.sold_slots ?? Math.max(pkg.total_slots - pkg.remaining_slots, 0)) : 0;
	const interestRate = pkg
		? parseFloat(pkg.monthly_commission_rate || pkg.min_profit_rate || '0').toFixed(1)
		: '0.0';
	const currentUserIdSet = useMemo(
		() => new Set(currentUsers.map(user => user.user_info.id)),
		[currentUsers]
	);
	const availableUsers = useMemo(
		() => users.filter(user => !currentUserIdSet.has(user.id)),
		[users, currentUserIdSet]
	);
	const totalPages = Math.max(1, Math.ceil(availableUsers.length / pageSize));
	const paginatedUsers = useMemo(() => {
		const start = (currentPage - 1) * pageSize;
		return availableUsers.slice(start, start + pageSize);
	}, [availableUsers, currentPage, pageSize]);

	useEffect(() => {
		setSelectedUserIds(prev => prev.filter(id => !currentUserIdSet.has(id)));
	}, [currentUserIdSet]);

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	return (
		<div className="font-sans">
			<Helmet>
				<title>{pkg ? t('plan.pageDetail.helmetTitleWithName', { name: pkg.name }) : t('plan.pageDetail.helmetTitle')}</title>
			</Helmet>

			<button
				onClick={() => navigate(-1)}
				className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline mb-5"
			>
				<ArrowLeft className="w-4 h-4" />
				{t('plan.pageDetail.backToList')}
			</button>

			<div className="flex items-center gap-3 mb-6">
				<div className="w-9 h-9 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0">
					<Package className="w-4 h-4 text-gray-600 dark:text-gray-300" />
				</div>
				<div>
					<h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('plan.pageDetail.title')}</h1>
					<p className="text-sm text-gray-500 dark:text-gray-400">{t('plan.pageDetail.subtitle')}</p>
				</div>
			</div>

			{loading ? (
				<div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-5 text-sm text-gray-500 dark:text-gray-400">
					{t('plan.pageDetail.loadingData')}
				</div>
			) : !pkg ? null : (
				<>
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
						<div className="lg:col-span-2 space-y-4">
							<div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-5">
								<h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('plan.pageDetail.basicInfo.title')}</h2>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
									<div>
										<p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('plan.pageDetail.basicInfo.packageName')}</p>
										<p className="text-sm font-semibold text-gray-900 dark:text-white">{pkg.name}</p>
									</div>
									<div>
										<p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('plan.pageDetail.basicInfo.packageType')}</p>
										<span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 uppercase">
											{pkg.type || 'STANDARD'}
										</span>
									</div>
									<div>
										<p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('plan.pageDetail.basicInfo.slotPrice')}</p>
										<p className="text-sm font-semibold text-gray-900 dark:text-white">{fmtVND(parentPrice)}</p>
									</div>
									<div>
										<p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('plan.pageDetail.basicInfo.status')}</p>
										<span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${pkg.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'}`}>
											{pkg.is_active ? t('plan.statusActive') : t('plan.statusInactive')}
										</span>
									</div>
								</div>

								<div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
									<p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('plan.pageDetail.basicInfo.description')}</p>
									<p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
										{pkg.description?.trim() || t('plan.pageDetail.basicInfo.noDescription')}
									</p>
								</div>
							</div>

							<div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-5">
								<h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('plan.pageDetail.slotAllocation.title')}</h2>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
									<div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/40">
										<p className="text-xs text-gray-500 dark:text-gray-400">{t('plan.pageDetail.slotAllocation.totalSlots')}</p>
										<p className="text-lg font-bold text-gray-900 dark:text-white mt-1">{pkg.total_slots}</p>
									</div>
									<div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/40">
										<p className="text-xs text-gray-500 dark:text-gray-400">{t('plan.pageDetail.slotAllocation.sold')}</p>
										<p className="text-lg font-bold text-orange-600 dark:text-orange-400 mt-1">{soldSlots}</p>
									</div>
									<div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/40">
										<p className="text-xs text-gray-500 dark:text-gray-400">{t('plan.pageDetail.slotAllocation.remaining')}</p>
										<p className="text-lg font-bold text-blue-600 dark:text-blue-400 mt-1">{pkg.remaining_slots}</p>
									</div>
								</div>
							</div>
						</div>

						<div className="space-y-4">
							<div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4">
								<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('plan.pageDetail.quickSummary.title')}</h3>
								<div className="space-y-2 text-sm">
									<div className="flex items-center justify-between gap-3">
										<span className="text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" />{t('plan.pageDetail.quickSummary.slotValue')}</span>
										<span className="font-medium text-gray-900 dark:text-white">{fmtVND(parentPrice)}</span>
									</div>
									<div className="flex items-center justify-between gap-3">
										<span className="text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5"><Percent className="w-3.5 h-3.5" />{t('plan.pageDetail.quickSummary.interestRate')}</span>
										<span className="font-medium text-gray-900 dark:text-white">{interestRate}{t('plan.detail.perMonth')}</span>
									</div>
									<div className="flex items-center justify-between gap-3">
										<span className="text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5" />{t('plan.pageDetail.quickSummary.duration')}</span>
										<span className="font-medium text-gray-900 dark:text-white">
											{pkg.duration_months > 0 ? `${pkg.duration_months} ${t('plan.detail.months')}` : t('plan.detail.untilCancel')}
										</span>
									</div>
								</div>
							</div>

							<div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4">
								<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('plan.pageDetail.timeline.title')}</h3>
								<div className="space-y-2 text-sm">
									<div className="flex justify-between gap-3">
										<span className="text-gray-500 dark:text-gray-400">{t('plan.pageDetail.timeline.createdAt')}</span>
										<span className="font-medium text-gray-900 dark:text-white text-right">{new Date(pkg.created_at).toLocaleString('vi-VN')}</span>
									</div>
									<div className="flex justify-between gap-3">
										<span className="text-gray-500 dark:text-gray-400">{t('plan.pageDetail.timeline.updatedAt')}</span>
										<span className="font-medium text-gray-900 dark:text-white text-right">{new Date(pkg.updated_at).toLocaleString('vi-VN')}</span>
									</div>
								</div>
							</div>

							<div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4">
								<p className="text-xs text-gray-500 dark:text-gray-400">
									{t('plan.pageDetail.maxCanAdd', { count: pkg.remaining_slots })}
								</p>
							</div>
						</div>
					</div>

					<div className="mt-6 bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700">
						<div className="p-5 border-b border-gray-200 dark:border-gray-700">
							<h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('plan.currentUsers.title')}</h3>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('plan.currentUsers.description')}</p>
						</div>

						<div className="overflow-x-auto">
							<table className="w-full text-left">
								<thead>
									<tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
										<th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('plan.currentUsers.columns.id')}</th>
										<th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('plan.currentUsers.columns.avatar')}</th>
										<th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('plan.currentUsers.columns.username')}</th>
										<th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('plan.currentUsers.columns.email')}</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 dark:divide-gray-700">
									{currentUsersLoading ? (
										<tr>
											<td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">{t('plan.currentUsers.loading')}</td>
										</tr>
									) : currentUsers.length === 0 ? (
										<tr>
											<td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">{t('plan.currentUsers.empty')}</td>
										</tr>
									) : (
										currentUsers.map(user => {
											const username = user.user_info.fullname || '—';
											const initials = username
												.split(' ')
												.filter(Boolean)
												.slice(0, 2)
												.map(part => part[0]?.toUpperCase())
												.join('') || 'U';

											return (
												<tr key={user.user_package_id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
													<td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{user.user_info.id}</td>
													<td className="px-4 py-3">
														{user.user_info.avatar ? (
															<img src={user.user_info.avatar} alt={username} className="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-gray-700" />
														) : (
															<div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300 flex items-center justify-center">
																{initials}
															</div>
														)}
													</td>
													<td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{username}</td>
													<td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{user.user_info.email}</td>
												</tr>
											);
										})
									)}
								</tbody>
							</table>
						</div>
					</div>

					<div className="mt-6 bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700">
						<div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
							<div>
								<h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('plan.pageDetail.usersList.title')}</h3>
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('plan.pageDetail.usersList.description')}</p>
							</div>
							<button
								type="button"
								onClick={handleAssignUsers}
								disabled={assigning || selectedUserIds.length === 0 || selectedUserIds.length > pkg.remaining_slots}
								className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-md font-medium text-sm transition-colors"
							>
								{assigning ? t('plan.pageDetail.assigning') : t('plan.pageDetail.addToPackage', { count: selectedUserIds.length })}
							</button>
						</div>

						<div className="overflow-x-auto">
							<table className="w-full text-left">
								<thead>
									<tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
										<th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase w-10"></th>
										<th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('plan.pageDetail.usersList.columns.id')}</th>
										<th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('plan.pageDetail.usersList.columns.name')}</th>
										<th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('plan.pageDetail.usersList.columns.avatar')}</th>
										<th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('plan.pageDetail.usersList.columns.email')}</th>
										<th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t('plan.pageDetail.usersList.columns.cccd')}</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 dark:divide-gray-700">
									{usersLoading ? (
										<tr>
											<td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">{t('plan.pageDetail.usersList.loading')}</td>
										</tr>
									) : availableUsers.length === 0 ? (
										<tr>
											<td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">{t('plan.pageDetail.usersList.empty')}</td>
										</tr>
									) : (
										paginatedUsers.map(user => {
											const checked = selectedUserIds.includes(user.id);
											const name = user.full_name_display || user.fullname || '—';
											const cccdValue = user.cccd || user.cccd_number || '—';
											const initials = name
												.split(' ')
												.filter(Boolean)
												.slice(0, 2)
												.map(part => part[0]?.toUpperCase())
												.join('') || 'U';

											return (
												<tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
													<td className="px-4 py-3">
														<input
															type="checkbox"
															checked={checked}
															onChange={() => toggleUser(user.id)}
															className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
														/>
													</td>
													<td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{user.id}</td>
													<td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{name}</td>
													<td className="px-4 py-3">
														{user.avatar ? (
															<img src={user.avatar} alt={name} className="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-gray-700" />
														) : (
															<div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300 flex items-center justify-center">
																{initials}
															</div>
														)}
													</td>
													<td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{user.email}</td>
													<td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{cccdValue}</td>
												</tr>
											);
										})
									)}
								</tbody>
							</table>
						</div>

						<div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
							<div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
								<span>{t('plan.pageDetail.pagination.rowsPerPage')}</span>
								<select
									value={pageSize}
									onChange={(e) => {
										setPageSize(Number(e.target.value));
										setCurrentPage(1);
									}}
									className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
								>
									{[10, 20, 50, 100].map(size => (
										<option key={size} value={size}>{size}</option>
									))}
								</select>
							</div>

							<div className="flex items-center gap-2">
								<button
									type="button"
									onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
									disabled={currentPage === 1 || usersLoading}
									className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{t('plan.pageDetail.pagination.prev')}
								</button>
								<span className="text-sm text-gray-600 dark:text-gray-400">
									{t('plan.pageDetail.pagination.page', { current: currentPage, total: totalPages })}
								</span>
								<button
									type="button"
									onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
									disabled={currentPage === totalPages || usersLoading}
									className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{t('plan.pageDetail.pagination.next')}
								</button>
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
